import React from 'react';
import PropTypes from 'prop-types';
import { TextLinkBack } from '@nebula/text-link';
import { ExternalLinkIcon } from '@nebula/icons/ui';
import { InformationAlert } from '@nebula/alert';
import { openAuthenticatedUrl } from '@ecs-uk/web-common-mobile-view-helpers';
import {
  BenefitsList,
  BenefitsSpan,
  ButtonGroup,
  CancelButton,
  CancelContainer,
  Container,
  EndDescription,
  IconContainer,
  InfoAlertContainer,
  InfoContainer,
  RetainSubscription,
  RetainSubscriptionContainer,
  StyledBodyLarge400,
  StyledBodyMedium500,
  StyledTitleLarge500,
  StyledTitleMedium500,
  TextContainer,
  TimeLabel,
} from './index.styles';
import { getBenefits, getSubscriptionName, formatDateToReadable, CANCELLATION_TYPES } from '../../constants';
import benefitsDocument from '../../../../constants/benefitsDocument';
import CalendarDeferredIcon from '../../../Icons/CalendarDeferredIcon';
import ImmediateCancelIcon from '../../../Icons/ImmediateCancelIcon';
import { trackCoolingOffCancelClick } from '../../../../helpers/switch/analytics';
import usePageView from '../../../../hooks/usePageView';

export default function CoolingOffPeriodStage({
  onBack,
  onKeepSubscription,
  isWebview,
  subscriptionFamily,
  nextBillingDate,
  goToConfirmCancel,
  setDeferred,
  setImmediate,
}) {
  const benefits = getBenefits(subscriptionFamily);
  const subscriptionName = getSubscriptionName(subscriptionFamily);
  const formattedNextBillingDate = formatDateToReadable(nextBillingDate);
  const FactSheetLink = `${benefitsDocument.baseUrl}/${benefitsDocument[subscriptionFamily]}`;

  const PAGE_NAME = 'ProdMoveCoolingOffDownsellPage';

  usePageView(PAGE_NAME);

  const handleDeferredCancel = () => {
    setDeferred();
    trackCoolingOffCancelClick(subscriptionFamily, CANCELLATION_TYPES.DEFERRED, 'initiated');
    goToConfirmCancel();
  };

  const handleImmediateCancel = () => {
    setImmediate();
    trackCoolingOffCancelClick(subscriptionFamily, CANCELLATION_TYPES.IMMEDIATE, 'initiated');
    goToConfirmCancel();
  };

  const handleKeepSubscription = () => {
    onKeepSubscription('selection');
  };

  const openFactSheetLink = url => event => {
    if (!isWebview) {
      return;
    }

    event.preventDefault();
    openAuthenticatedUrl(new URL(url));
  };

  return (
    <Container>
      <InfoContainer>
        {!isWebview && <TextLinkBack onClick={onBack}>Back</TextLinkBack>}
        <StyledTitleLarge500>{`You’re about to cancel your ${subscriptionName} subscription`}</StyledTitleLarge500>
        <StyledBodyLarge400>Moving to a free account means you’ll lose access to:</StyledBodyLarge400>
        <BenefitsList>
          {benefits.map(benefit => (
            <li key={benefit}>{benefit}</li>
          ))}
        </BenefitsList>
        <BenefitsSpan
          href={FactSheetLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={openFactSheetLink(FactSheetLink)}
        >
          See all the benefits of {subscriptionName} <ExternalLinkIcon iconColor="#255AF0" />
        </BenefitsSpan>
      </InfoContainer>
      <CancelContainer>
        <StyledTitleMedium500>When do you want to cancel?</StyledTitleMedium500>

        <ButtonGroup role="none">
          <CancelButton
            id="deferredDownsellButton"
            onClick={handleDeferredCancel}
            aria-label={`Cancel on ${formattedNextBillingDate}. Your benefits stay active until then.`}
          >
            <IconContainer aria-hidden="true">
              <CalendarDeferredIcon />
            </IconContainer>
            <TextContainer aria-hidden="true">
              <TimeLabel>On {formattedNextBillingDate}</TimeLabel>
              <EndDescription>Your benefits stay active until then</EndDescription>
            </TextContainer>
          </CancelButton>

          <CancelButton
            id="immediateDownsellButton"
            onClick={handleImmediateCancel}
            aria-label="Cancel now. Your benefits end today."
          >
            <IconContainer aria-hidden="true">
              <ImmediateCancelIcon />
            </IconContainer>
            <TextContainer aria-hidden="true">
              <TimeLabel>Now</TimeLabel>
              <EndDescription>Your benefits end today</EndDescription>
            </TextContainer>
          </CancelButton>
        </ButtonGroup>

        <InfoAlertContainer aria-label={`Your current billing period ends on ${formattedNextBillingDate}`}>
          <div aria-hidden="true">
            <InformationAlert title="Your current billing period ends" message={formattedNextBillingDate} />
          </div>
        </InfoAlertContainer>

        <RetainSubscriptionContainer>
          <StyledBodyMedium500>
            Not ready to cancel?{'  '}
            <RetainSubscription onClick={handleKeepSubscription}>{`Keep ${subscriptionName}`}</RetainSubscription>
          </StyledBodyMedium500>
        </RetainSubscriptionContainer>
      </CancelContainer>
    </Container>
  );
}

CoolingOffPeriodStage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onKeepSubscription: PropTypes.func.isRequired,
  isWebview: PropTypes.bool,
  subscriptionFamily: PropTypes.string.isRequired,
  nextBillingDate: PropTypes.string,
  goToConfirmCancel: PropTypes.func.isRequired,
  setDeferred: PropTypes.func.isRequired,
  setImmediate: PropTypes.func.isRequired,
};

CoolingOffPeriodStage.defaultProps = {
  isWebview: false,
  nextBillingDate: null,
};
